import React from 'react';
import { Form, Button, Row, Col, CardBody, Card } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UserService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import { useTranslation } from 'react-i18next';
import FormErrorSummary from '../../shared/components/FormErrorSummary';

const passwordSchema = yup.object().shape({
    password: yup.string().required('Password is required'),
    confirmPassword: yup.string().oneOf([yup.ref('password'), undefined], 'Passwords must match').required('Confirm Password is required')
});

interface PasswordState {
    password: string;
    confirmPassword: string;
}

const ChangePasswordForm: React.FC<{ userId: number }> = ({ userId }) => {
    const { t } = useTranslation(); // Initialize translation hook
    const { control, handleSubmit, formState: { errors } } = useForm<PasswordState>({
        resolver: yupResolver(passwordSchema),
        defaultValues: {
            password: "",
            confirmPassword: ""
        }
    });

    const onPasswordSubmit = async (data: PasswordState) => {
        try {
            await UserService.changePassword({ password: data.password }, userId);
            SuccessToast(t('user.passwordUpdated'));
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <Card className="rounded-top-0 border-top-0">
            <CardBody>
                <Form onSubmit={handleSubmit(onPasswordSubmit)}>
                    <FormErrorSummary errors={errors} />
                    <Row className="g-3">
                        <Col sm={6} xl={3}>
                            <div>
                                <label className="form-label">{t('user.password')}</label>
                                <Controller
                                    name="password"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                            placeholder={t('user.enterPassword')}
                                            {...field}
                                        />
                                    )}
                                />
                                {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div>
                                <label className="form-label">{t('user.confirmPassword')}</label>
                                <Controller
                                    name="confirmPassword"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                            placeholder={t('user.confirmPasswordPlaceholder')}
                                            {...field}
                                        />
                                    )}
                                />
                                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword.message}</div>}
                            </div>
                        </Col>
                        <Col sm={12} className="mt-4">
                            <Button type="submit" variant="primary">
                                {t('user.changePassword')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </CardBody>
        </Card>
    );
};

export default ChangePasswordForm;